/** Publishing an approval route, from the Admin's side — **ADR-0067, decision 1A** (card C-D).
 *
 * `POST /approval-routes` existed, was tested, and nothing called it: a real workspace had no
 * route, so the record page said *"No review route is configured… nothing can be submitted"* and
 * no figure could ever be approved. This is the screen's half.
 *
 * **Three decisions are encoded here rather than in a component, so a component cannot undo them.**
 *
 * - **A stage has no position.** The server assigns `1..n` in the order given, so a gap or a
 *   duplicate is not expressible. `PublishStageIn` carries no `position`, and the type assertion
 *   below makes the day the schema gains one a compile error here instead of a field somebody adds
 *   to a form without asking whether the server still wants to decide it.
 * - **Only an Admin publishes.** `mayPublishRoute` is the client's reading of the server's
 *   `MAY_PUBLISH_ROUTE`; the server is the authority, and this exists so a Contributor is told who
 *   can rather than shown a control that 403s (design rule 6, `KEEP.md` 17).
 * - **A read that fails throws.** `return data ?? []` made a failed read look like *"no route is
 *   defined"* ten times over in this codebase (`KEEP.md` 19). `usePublishedRoutes` has no
 *   defaulting, so `Loaded` can tell loading, failed and empty apart.
 *
 * **Not here:** editing or retiring a route. A published version is never edited (ADR-0037
 * decision 2); a change is the next version, published the same way.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type PublishedRoute = components["schemas"]["PublishedRouteOut"];
export type RouteRow = components["schemas"]["RouteOut"];
export type RouteKind = components["schemas"]["RecordKind"];
type PublishBody = components["schemas"]["PublishRouteIn"];
type PublishStage = components["schemas"]["PublishStageIn"];

/** Compile-time: the stage the server accepts has no `position`. If the schema ever adds one this
 * line stops compiling, which is the point at which this file has to be read again. */
const STAGE_HAS_NO_POSITION: "position" extends keyof PublishStage ? never : true = true;
void STAGE_HAS_NO_POSITION;

/** The kinds a route can be written for. The set of kinds a record *has* is the server's enum; this
 * is the subset a person can sensibly review, in the order a reader looks for them. A kind not
 * listed is not unsupported by the server — it is one nobody has asked to route. */
export const ROUTE_KINDS: readonly RouteKind[] = [
  "activity_record",
  "disclosure_statement",
  "filing_obligation",
  "climate_risk",
  "initiative",
  "document",
];

/** The server's `route_publishing.DECIDING_ROLES`: the only roles `decide` admits, and so the only
 * roles a stage may name. A stage naming anyone else is a route nobody can complete. */
const DECIDING_ROLES: readonly string[] = ["admin", "approver"];

/** Whether this role may publish. **Admin only** (ADR-0067 decision 1A): the person who defines a
 * route is not the person it will be used against. */
export function mayPublishRoute(role: string | undefined): boolean {
  return role === "admin";
}

/** A member as far as picking a reviewer is concerned. */
export interface ReviewerCandidate {
  user_id: string;
  display_name: string;
  role: string;
  status: string;
}

/** Who could be named on a stage today. **A convenience, not the rule** — the server checks the
 * user's status, the role and the membership's dates, and answers the same refusal for a person who
 * does not exist as for one who cannot decide. This only keeps the picker from offering people the
 * server is certain to refuse. */
export function eligibleReviewers<T extends ReviewerCandidate>(members: readonly T[]): T[] {
  return members.filter((m) => m.status === "active" && DECIDING_ROLES.includes(m.role));
}

export interface StageDraft {
  name: string;
  reviewerId: string;
}

export interface RouteDraft {
  key: string;
  name: string;
  appliesTo: RouteKind;
  requiresSeparation: boolean;
  stages: StageDraft[];
}

/** The slug the server accepts for a key (`collaboration.RouteKey`). Checked on the client only so
 * the form can say what a key looks like before it is refused with a generic validation error. */
export const ROUTE_KEY_PATTERN = /^[a-z0-9][a-z0-9_.-]{0,59}$/;

/** The request body. **Stages are `{name, reviewer_id}` and nothing else**, in the order drawn:
 * the order *is* the position, and the server numbers them. */
export function publishBody(draft: RouteDraft): PublishBody {
  return {
    key: draft.key.trim(),
    name: draft.name.trim(),
    applies_to: draft.appliesTo,
    requires_separation: draft.requiresSeparation,
    stages: draft.stages.map((s): PublishStage => ({ name: s.name.trim(), reviewer_id: s.reviewerId })),
  };
}

export const routeKeys = {
  /** Every route in the workspace, every kind: the Admin's list. */
  all: () => ["approval-routes"] as const,
};

/** Every route this workspace has, newest version first within a key (the server's order).
 *
 * `enabled` is the caller's: a role that may not publish has no reason to read the list, and the
 * list names reviewers by id. `retry: false` because a 403 is an answer. */
export function usePublishedRoutes(enabled: boolean) {
  return useQuery({
    queryKey: routeKeys.all(),
    enabled,
    retry: false,
    queryFn: async (): Promise<RouteRow[]> => {
      const { data, error, response } = await api.GET("/api/v1/approval-routes", {});
      // Swallowed, this reads as *"no approval route is defined"* — which an Admin acts on by
      // publishing another, and which tells a reviewer nothing can be submitted.
      if (error) throw refusalFrom(response, error, "The approval routes could not be read.");
      // The generated type is `RouteOut[]`; a body that is not an array is a server that answered
      // with something else, and an empty array here would be the same lie.
      if (!Array.isArray(data)) throw new Error("The approval routes could not be read.");
      return data;
    },
  });
}

/** Publish one version of a route.
 *
 * On success two caches are dropped: the Admin's list, and **the per-kind route sets the record
 * page offers** (`["work", "routes", kind]`, `api/work.ts`). Without the second, the record page
 * goes on saying *"No review route is configured"* for the length of its stale time after the route
 * exists — the acceptance criterion this card is for.
 *
 * A repeat of the latest version is answered `already_published: true` with 201 and writes
 * nothing (ADR-0047); the caller reads that flag rather than assuming a version was created.
 */
export function usePublishRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: RouteDraft): Promise<PublishedRoute> => {
      const { data, error, response } = await api.POST("/api/v1/approval-routes", {
        body: publishBody(draft),
      });
      if (error) throw refusalFrom(response, error, "That route could not be published.");
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: routeKeys.all() }),
        queryClient.invalidateQueries({ queryKey: ["work", "routes"] }),
      ]);
    },
  });
}

/* ------------------------------------------------------------------------------------------------
 * The reviewer's queue — **card C-H, ADR-0067** (the half `BUILD/blocked/C-D.md` refused).
 *
 * `GET /api/v1/approval-requests?awaiting_me=true`. The server filters to requests that are waiting
 * on a reviewer **and** whose *current* stage names the signed-in person, oldest first. That
 * filtering is the whole reason the queue is a request and not a walk over the registers: a client
 * that assembled it would stop where the register it could see stopped, and *"nothing is waiting on
 * you"* would be printed over work that is waiting.
 *
 * **It was hand-typed, and is not any more.** While the generated schema predated this route, the
 * path was declared here by hand beside a compile-time assertion that would stop compiling the day
 * the schema gained it. `make api-types` ran on 2026-10-23, the assertion fired, and the block it
 * guarded was deleted — which is the whole point of writing a tripwire instead of a comment. A
 * second copy of a type the compiler already has is a copy that will eventually disagree.
 * ----------------------------------------------------------------------------------------------- */

/** One request in the queue: the existing request shape plus what a row needs, so a row costs no
 * second read per request (the N+1 this endpoint exists to refuse). */
/** One request in the queue. **Now the generated type** — `AwaitingRequestOut` carries
 * `record_kind`, `submitted_at` and `stage_name` itself, so the hand-written intersection that
 * stood here is gone along with the `QueuePaths` block and its tripwire. The alias is kept because
 * callers name it and the shorter name reads better at a call site; it declares nothing. */
export type AwaitingRequest = components["schemas"]["AwaitingRequestOut"];


/** The roles `decide` admits — the only people who can be named on a stage, and so the only people
 * who have a queue. **The client's reading of the server's rule**, as `mayPublishRoute` is: the
 * server's 403 is the authority, and this exists so a Viewer or an Auditor is told there is no
 * queue for their role instead of being shown an empty list that reads as *nothing is owed*. */
export function mayHaveQueue(role: string | undefined): boolean {
  return role !== undefined && DECIDING_ROLES.includes(role);
}

export const queueKeys = {
  /** The signed-in person's queue. No identity in the key: the server derives whose it is from the
   * session, and switching workspace clears the cache (`client.ts`). */
  awaitingMe: () => ["approval-requests", "awaiting-me"] as const,
};

/** Requests waiting on the signed-in reviewer's decision.
 *
 * **A failed read throws.** `return data ?? []` would make a 500 render as *"Nothing is waiting on
 * you."* over work that is waiting — the defect this whole card exists to avoid (`KEEP.md` 19).
 * `Loaded` can only tell loading, failed and empty apart if this hook never defaults.
 *
 * `enabled` is the caller's: a role with no queue makes no request. `refetchOnMount: "always"`
 * because a queue read before a decision was taken is a list that still offers the decided request.
 */
export function useAwaitingMe(enabled: boolean) {
  return useQuery({
    queryKey: queueKeys.awaitingMe(),
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async (): Promise<AwaitingRequest[]> => {
      const { data, error, response } = await api.GET("/api/v1/approval-requests", {
        params: { query: { awaiting_me: true } },
      });
      if (error) throw refusalFrom(response, error, "Your approval queue could not be read.");
      // A body that is not an array is a server that answered with something else. Defaulting it
      // to `[]` would be the same lie as swallowing the error.
      if (!Array.isArray(data)) throw new Error("Your approval queue could not be read.");
      return data;
    },
  });
}

/* ------------------------------------------------------------------------------------------------
 * The other two lists (wave 3 B4): what this person submitted, and what has been decided here.
 *
 * Both are server-paged envelopes (`items`, `total`, `limit`, `offset`), and **the total is the
 * server's**: a screen says "showing 50 of 212" from `total`, never from the length of the page it
 * holds. Neither hook defaults: a failed read throws, so a screen draws it as a failure and never as
 * "you have submitted nothing".
 *
 * `submitted-by-me` is for the roles that can submit (Admin, Approver, Contributor); `decided` is for
 * the roles that decide (Admin, Approver). A role outside the set makes no request (`enabled`), so
 * the server's refusal is never provoked to find out.
 * ----------------------------------------------------------------------------------------------- */

export type ListedRequest = components["schemas"]["ListedRequestOut"];
export type ListedRequests = components["schemas"]["ListedRequestsOut"];

/** The roles `submitted-by-me` admits (`collaboration.MAY_WRITE`): a mirror, and the server decides. */
export function maySubmit(role: string | undefined): boolean {
  return role === "admin" || role === "approver" || role === "contributor";
}

export const LIST_PAGE = 50;

function readListed(value: unknown, what: string): ListedRequests {
  if (
    value === null ||
    typeof value !== "object" ||
    !Array.isArray((value as { items?: unknown }).items) ||
    typeof (value as { total?: unknown }).total !== "number"
  ) {
    throw new Error(`${what} came back in a shape this screen cannot read.`);
  }
  return value as ListedRequests;
}

export function useSubmittedByMe(enabled: boolean) {
  return useQuery({
    queryKey: ["approval-requests", "submitted-by-me"] as const,
    enabled,
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<ListedRequests> => {
      const { data, error, response } = await api.GET("/api/v1/approval-requests/submitted-by-me", {
        params: { query: { limit: LIST_PAGE } },
      });
      if (error) throw refusalFrom(response, error, "The requests you submitted could not be read.");
      return readListed(data, "The list of requests you submitted");
    },
  });
}

export function useDecided(enabled: boolean, decidedByMe: boolean) {
  return useQuery({
    queryKey: ["approval-requests", "decided", decidedByMe] as const,
    enabled,
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<ListedRequests> => {
      const { data, error, response } = await api.GET("/api/v1/approval-requests/decided", {
        params: { query: { limit: LIST_PAGE, decided_by_me: decidedByMe } },
      });
      if (error) throw refusalFrom(response, error, "The decided requests could not be read.");
      return readListed(data, "The list of decided requests");
    },
  });
}

/** The newest version of each route key. `GET /approval-routes` returns every version, and a
 * superseded one stays in the table on purpose (a request cites the version it was submitted
 * under). Offered in a picker it is a second option with the same name - the journey walk
 * (2026-10-31) saw "Standard review" twice - and choosing the older one sends the record to the
 * reviewer the route named before it was edited. */
export function currentRoutes<T extends { key: string; version: number }>(routes: readonly T[]): T[] {
  const newest = new Map<string, T>();
  for (const route of routes) {
    const seen = newest.get(route.key);
    if (seen === undefined || route.version > seen.version) newest.set(route.key, route);
  }
  return [...newest.values()];
}
